import { useEffect, useState, lazy, Suspense } from "react";
import { useParams, useLocation, Routes, Route } from "react-router-dom";
import http, { API_BASE_URL } from "../api/http";
import { mockStoreData } from "./store/mockStoreData";
import { getPublicStoreCategories } from "../api/product_management/productApi";
import { CartProvider } from "./store/context/CartContext";
import axios from "axios";
import ScrollToTop from "./store/templates/editorial/ScrollToTop.jsx";
import PageLoader from "./store/components/PageLoader.jsx";
import NotFound from "./NotFound.jsx";

// ================================
// MODERN TEMPLATE
// ================================
const ModernHomeTemplate = lazy(() => import("./store/templates/modern/Home"));
const ModernAboutTemplate = lazy(
  () => import("./store/templates/modern/About"),
);
const ModernContactTemplate = lazy(
  () => import("./store/templates/modern/Contact"),
);
const ModernCategoryProductsPage = lazy(
  () => import("./store/templates/modern/CategoryProductsPage"),
);
const ModernStoreSuspendedPage = lazy(
  () => import("./store/templates/modern/common/StoreSuspendedPage.jsx"),
);

// ================================
// CREAM TEMPLATE
// ================================
const CreamHomeTemplate = lazy(() => import("./store/templates/cream/Home"));
const CreamAboutTemplate = lazy(() => import("./store/templates/cream/About"));
const CreamContactTemplate = lazy(
  () => import("./store/templates/cream/Contact"),
);
const CreamCategoryProductsPage = lazy(
  () => import("./store/templates/cream/CategoryProductsPage"),
);
const CreamStoreSuspendedPage = lazy(
  () => import("./store/templates/cream/common/StoreSuspendedPage.jsx"),
);

// ================================
// EDITORIAL TEMPLATE
// ================================
const EditorialHomeTemplate = lazy(
  () => import("./store/templates/editorial/Home"),
);
const EditorialAboutTemplate = lazy(
  () => import("./store/templates/editorial/About"),
);
const EditorialContactTemplate = lazy(
  () => import("./store/templates/editorial/Contact"),
);
const EditorialCategoryProductsPage = lazy(
  () => import("./store/templates/editorial/CategoryProductsPage"),
);
const EditorialStoreSuspendedPage = lazy(
  () => import("./store/templates/editorial/common/StoreSuspendedPage.jsx"),
);

// Editorial commerce pages
const EditorialProductPage = lazy(
  () => import("./store/templates/editorial/common/ProductPage"),
);
const EditorialCartPage = lazy(
  () => import("./store/templates/editorial/common/CartPage"),
);
const EditorialCheckoutPage = lazy(
  () => import("./store/templates/editorial/common/CheckoutPage"),
);
const EditorialOrderTrackingPage = lazy(
  () => import("./store/templates/editorial/common/OrderTracking"),
);

// ================================
// SHARED / EXISTING COMMERCE PAGES
// ================================
const CreamProductPage = lazy(
  () => import("./store/templates/cream/common/ProductPage"),
);
const CreamCartPage = lazy(
  () => import("./store/templates/cream/common/CartPage"),
);
const CreamCheckoutPage = lazy(
  () => import("./store/templates/cream/common/CheckoutPage"),
);
const CreamOrderTrackingPage = lazy(
  () => import("./store/templates/cream/common/OrderTracking"),
);

// Modern commerce pages
const ModernProductPage = lazy(
  () => import("./store/templates/modern/common/ProductPage"),
);
const ModernCartPage = lazy(
  () => import("./store/templates/modern/common/CartPage"),
);
const ModernCheckoutPage = lazy(
  () => import("./store/templates/modern/common/CheckoutPage"),
);
const ModernOrderTrackingPage = lazy(
  () => import("./store/templates/modern/common/OrderTracking"),
);

const TEMPLATE_IDS = {
  modern: 1,
  cream: 3,
  editorial: 5,
};

function RouteChangeLoader() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);

    const timer = window.setTimeout(() => {
      setVisible(false);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search, location.hash]);

  return visible ? <PageLoader /> : null;
}

const CUSTOM_DOMAINS = {
  "arayeshiarosshik.ir": "arayeshiarosshik",
  "www.arayeshiarosshik.ir": "arayeshiarosshik",
};

export default function StoreRenderer() {
  const params = useParams();
  const currentHost = window.location.hostname.toLowerCase();

  const customSlug = CUSTOM_DOMAINS[currentHost];
  // فقط اگر دامنه اختصاصی بود override کن، وگرنه همون رفتار قبلی
  const slug = customSlug || params.slug;

  const [storeData, setStoreData] = useState(null);
  const [storeId, setStoreId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // وضعیت‌های مربوط به تعلیق یا پایان اشتراک فروشگاه
  const [suspended, setSuspended] = useState(false);

  const [suspendedMeta, setSuspendedMeta] = useState({
    storeName: null,
    storeSlug: slug,
  });

  const [suspendedTemplateId, setSuspendedTemplateId] = useState(
    TEMPLATE_IDS.modern,
  );

  const isCreamStore = slug === "cream";
  const isDemoStore = slug === "modern";
  const isEditorialStore = slug === "editorial";

  const getImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${API_BASE_URL}${url}`;
  };

  useEffect(() => {
    const registerVisit = async () => {
      try {
        await axios.post(`${API_BASE_URL}/api/stores/visits/${storeId}/`);
      } catch (error) {
        console.error("Failed to register visit", error);
      }
    };

    if (storeId) {
      registerVisit();
    }
  }, [storeId]);

  // تابع کمکی برای تشخیص تعلیق از روی پاسخ موفقیت‌آمیز اما دارای وضعیت تعلیق
  const isSuspendedFromPayload = (data) => {
    if (!data || typeof data !== "object") return false;

    console.log("[Debug] API Success Payload:", data);

    const isSuspendedFlag =
      data.is_suspended === true ||
      data.store_suspended === true ||
      data.status === "suspended" ||
      data.store_status === "suspended";

    if (isSuspendedFlag) return true;

    if (data.end_date) {
      const expireDate = new Date(data.end_date);
      const today = new Date();
      if (expireDate < today) return true;
    }

    if (data.remaining_days !== undefined && data.remaining_days !== null) {
      if (Number(data.remaining_days) <= 0) return true;
    }

    if (
      data.subscription_active === false ||
      data.is_subscription_active === false
    ) {
      return true;
    }

    return false;
  };

  // تابع کمکی برای تست خطاهای بازگشتی
  const isSuspendedFromError = (error) => {
    console.log("[Debug] API Error Object:", error);

    if (error.response) {
      console.log("[Debug] API Error Status:", error.response.status);
      console.log("[Debug] API Error Data:", error.response.data);
    }

    const status = error.response?.status;
    const data = error.response?.data;

    if (!data && status !== 403 && status !== 410) return false;

    const code = (data?.code || data?.error_code || "")
      .toString()
      .toLowerCase();

    const detail = (data?.detail || data?.message || "").toString();

    if (status === 410) return true;

    if (status === 403 && /subscription|اشتراک|suspend|تعلیق/i.test(detail)) {
      return true;
    }

    if (
      [
        "store_suspended",
        "subscription_expired",
        "subscription_inactive",
        "suspended",
      ].includes(code)
    ) {
      return true;
    }

    return data?.is_suspended === true || data?.subscription_active === false;
  };

  useEffect(() => {
    if (!slug) return;

    if (isDemoStore || isCreamStore || isEditorialStore) {
      setStoreId(0);

      setStoreData({
        ...mockStoreData,
        store: {
          ...mockStoreData.store,
          id: 0,
          store_id: 0,
          store_slug: slug,
          template: isEditorialStore
            ? TEMPLATE_IDS.editorial
            : mockStoreData.store?.template,
        },
      });

      setSuspended(false);
      setLoading(false);
      return;
    }

    fetchStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchStore = async () => {
    try {
      console.log("fetchStore START");

      setLoading(true);
      setNotFound(false);
      setSuspended(false);

      const [homeRes, aboutRes, contactRes, footerRes] = await Promise.all([
        http.get(`/store-management/store-home/${slug}/`),
        http.get(`/store-management/store-about/${slug}/`),
        http.get(`/store-management/store-contact/${slug}/`),
        http.get(`/store-management/store-footer/${slug}/`),
      ]);

      const homeData = homeRes.data;
      const aboutData = aboutRes.data;
      const footerData = footerRes.data;
      const contactData = contactRes.data;

      if (isSuspendedFromPayload(homeData)) {
        setSuspendedMeta({
          storeName: homeData.store_name || null,
          storeSlug: homeData.store_slug || slug,
        });

        setSuspendedTemplateId(
          Number(homeData.template) || TEMPLATE_IDS.modern,
        );

        setSuspended(true);
        setLoading(false);
        return;
      }

      setStoreId(homeData.store_id);

      const categoriesRes = await getPublicStoreCategories(homeData.store_id);

      setStoreData({
        ...mockStoreData,
        store: {
          id: homeData.store_id,
          store_id: homeData.store_id,
          store_name: homeData.store_name,
          store_slug: homeData.store_slug,
          has_pro_online: homeData.has_pro_online,
          template: homeData.template,
          logo: getImageUrl(homeData.logo),
          phone: homeData.phone,
          mobile: homeData.mobile,
          address: homeData.address,
          footer_sections: footerData.sections || [],
        },
        categories: categoriesRes.data || [],
        sections: [
          ...(homeData.sections || []),
          ...(aboutData.sections || []),
          ...(contactData.sections || []),
        ],
        home_banners: homeData.home_banners || [],
      });
    } catch (error) {
      if (isSuspendedFromError(error)) {
        const errorData = error.response?.data;

        setSuspendedMeta({
          storeName: errorData?.store_name || null,
          storeSlug: errorData?.store_slug || slug,
        });

        setSuspendedTemplateId(
          Number(errorData?.template) || TEMPLATE_IDS.modern,
        );

        setSuspended(true);
      } else if (error.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <PageLoader />;

  let StoreSuspendedPage = ModernStoreSuspendedPage;
  if (suspendedTemplateId === TEMPLATE_IDS.cream) {
    StoreSuspendedPage = CreamStoreSuspendedPage;
  } else if (suspendedTemplateId === TEMPLATE_IDS.editorial) {
    StoreSuspendedPage = EditorialStoreSuspendedPage;
  }

  if (suspended) {
    return (
      <StoreSuspendedPage
        storeName={suspendedMeta.storeName}
        storeSlug={suspendedMeta.storeSlug}
      />
    );
  }

  if (notFound) {
    return <NotFound title="فروشگاه پیدا نشد" storeSlug={slug} />;
  }

  if (!storeData || storeId === null) return null;

  // =========================================================
  // TEMPLATE SELECTION
  // =========================================================
  const templateId = Number(storeData?.store?.template);

  console.log("🔥 TEMPLATE DEBUG:", {
    slug,
    rawTemplate: storeData?.store?.template,
    rawType: typeof storeData?.store?.template,
    templateId,
    modern: TEMPLATE_IDS.modern,
    cream: TEMPLATE_IDS.cream,
    editorial: TEMPLATE_IDS.editorial,
  });

  let HomeTemplate;
  let AboutTemplate;
  let ContactTemplate;
  let CategoryProductsPage;

  // Commerce pages
  let ProductPage;
  let CartPage;
  let CheckoutPage;
  let OrderTrackingPage;

  switch (templateId) {
    case TEMPLATE_IDS.editorial:
      HomeTemplate = EditorialHomeTemplate;
      AboutTemplate = EditorialAboutTemplate;
      ContactTemplate = EditorialContactTemplate;
      CategoryProductsPage = EditorialCategoryProductsPage;
      ProductPage = EditorialProductPage;
      CartPage = EditorialCartPage;
      CheckoutPage = EditorialCheckoutPage;
      OrderTrackingPage = EditorialOrderTrackingPage;
      break;

    case TEMPLATE_IDS.cream:
      HomeTemplate = CreamHomeTemplate;
      AboutTemplate = CreamAboutTemplate;
      ContactTemplate = CreamContactTemplate;
      CategoryProductsPage = CreamCategoryProductsPage;
      ProductPage = CreamProductPage;
      CartPage = CreamCartPage;
      CheckoutPage = CreamCheckoutPage;
      OrderTrackingPage = CreamOrderTrackingPage;
      break;

    case TEMPLATE_IDS.modern:
    default:
      HomeTemplate = ModernHomeTemplate;
      AboutTemplate = ModernAboutTemplate;
      ContactTemplate = ModernContactTemplate;
      CategoryProductsPage = ModernCategoryProductsPage;
      ProductPage = ModernProductPage;
      CartPage = ModernCartPage;
      CheckoutPage = ModernCheckoutPage;
      OrderTrackingPage = ModernOrderTrackingPage;
      break;
  }

  return (
    <CartProvider storeId={storeId}>
      <Suspense fallback={<PageLoader />}>
        <RouteChangeLoader />
        <ScrollToTop />

        <Routes>
          {/* -----------------------------------------------------------
              1. حالت روت‌های تمیز دامنه اختصاصی (مثل /about یا /category/...)
             ----------------------------------------------------------- */}
          <Route path="/" element={<HomeTemplate {...storeData} />} />
          <Route path="about" element={<AboutTemplate {...storeData} />} />
          <Route path="contact" element={<ContactTemplate {...storeData} />} />
          <Route
            path="category/:categorySlug"
            element={<CategoryProductsPage {...storeData} />}
          />
          <Route path="product/:id" element={<ProductPage {...storeData} />} />
          <Route
            path="cart"
            element={
              <CartPage
                store={storeData.store}
                categories={storeData.categories}
              />
            }
          />
          <Route
            path="checkout"
            element={
              <CheckoutPage
                store={storeData.store}
                categories={storeData.categories}
              />
            }
          />
          <Route
            path="track-order"
            element={
              <OrderTrackingPage
                store={storeData.store}
                categories={storeData.categories}
              />
            }
          />

          {/* -----------------------------------------------------------
              2. حالت دامنه اختصاصی با پیشوند قالب (/store/modern/... و بقیه قالب‌ها)
                 بدون نیاز به دست زدن به کامپوننت‌های Header, Footer یا بقیه صفحات
             ----------------------------------------------------------- */}
          <Route path="store/:tpl" element={<HomeTemplate {...storeData} />} />
          <Route
            path="store/:tpl/about"
            element={<AboutTemplate {...storeData} />}
          />
          <Route
            path="store/:tpl/contact"
            element={<ContactTemplate {...storeData} />}
          />
          <Route
            path="store/:tpl/category/:categorySlug"
            element={<CategoryProductsPage {...storeData} />}
          />
          <Route
            path="store/:tpl/product/:id"
            element={<ProductPage {...storeData} />}
          />
          <Route
            path="store/:tpl/cart"
            element={
              <CartPage
                store={storeData.store}
                categories={storeData.categories}
              />
            }
          />
          <Route
            path="store/:tpl/checkout"
            element={
              <CheckoutPage
                store={storeData.store}
                categories={storeData.categories}
              />
            }
          />
          <Route
            path="store/:tpl/track-order"
            element={
              <OrderTrackingPage
                store={storeData.store}
                categories={storeData.categories}
              />
            }
          />

          {/* -----------------------------------------------------------
              3. حالت استاندارد dokana.ir/:slug برای تمامی فروشگاه‌های دیگر سیستم
             ----------------------------------------------------------- */}
          <Route path=":slug" element={<HomeTemplate {...storeData} />} />
          <Route
            path=":slug/about"
            element={<AboutTemplate {...storeData} />}
          />
          <Route
            path=":slug/contact"
            element={<ContactTemplate {...storeData} />}
          />
          <Route
            path=":slug/category/:categorySlug"
            element={<CategoryProductsPage {...storeData} />}
          />
          <Route
            path=":slug/product/:id"
            element={<ProductPage {...storeData} />}
          />
          <Route
            path=":slug/cart"
            element={
              <CartPage
                store={storeData.store}
                categories={storeData.categories}
              />
            }
          />
          <Route
            path=":slug/checkout"
            element={
              <CheckoutPage
                store={storeData.store}
                categories={storeData.categories}
              />
            }
          />
          <Route
            path=":slug/track-order"
            element={
              <OrderTrackingPage
                store={storeData.store}
                categories={storeData.categories}
              />
            }
          />
        </Routes>
      </Suspense>
    </CartProvider>
  );
}
